import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface User {
  uid: string;
  email: string;
  displayName: string | null;
  role: string;
}

interface UserDetails {
  fullName: string;
  email: string;
  phone: string;
  profileUpdated: boolean;
  adminVerified: boolean;
  memberStatus: string;
  [key: string]: any;
}

interface AuthContextType {
  currentUser: User | null;
  userDetails: UserDetails | null;
  loading: boolean;
  beginLogin: (email: string, password: string) => Promise<{ otpRequired: boolean; message?: string }>;
  verifyLoginOtp: (email: string, otp: string) => Promise<boolean>;
  googleLogin: (credential: string) => Promise<'ok' | 'failed'>;
  register: (email: string, password: string, fullName: string, phone?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUserDetails: () => Promise<void>;
}

const CloudflareAuthContext = createContext<AuthContextType | null>(null);

export const useCloudflareAuth = () => {
  const context = useContext(CloudflareAuthContext);
  if (!context) {
    throw new Error('useCloudflareAuth must be used within a CloudflareAuthProvider');
  }
  return context;
};

export const CloudflareAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Map snake_case API response to camelCase for frontend
  const mapUserDetails = (details: any): UserDetails => {
    return {
      fullName: details.full_name || details.fullName || '',
      email: details.email || '',
      phone: details.phone || '',
      profileUpdated: Boolean(details.profile_updated ?? details.profileUpdated ?? false),
      adminVerified: details.admin_verified || details.adminVerified || false,
      memberStatus: details.member_status || details.memberStatus || 'pending',
      memberType: details.member_type || details.memberType || '',
      membershipPlanId: details.membership_plan_id || details.membershipPlanId || '',
      membershipPaymentStatus: details.membership_payment_status || details.membershipPaymentStatus || 'pending',
      membershipPaidAt: details.membership_paid_at || details.membershipPaidAt || '',
      designation: details.designation || '',
      enrollmentNumber: details.enrollment_number || details.enrollmentNumber || '',
      photo: details.photo || details.photo_url || '',
      validFrom: details.valid_from || details.validFrom || '',
      validUntil: details.valid_until || details.validUntil || '',
      dateOfBirth: details.date_of_birth || details.dateOfBirth || '',
      gender: details.gender || '',
      bloodGroup: details.blood_group || details.bloodGroup || '',
      education: details.education || '',
      profession: details.profession || '',
      fatherOrHusbandName: details.father_or_husband_name || details.fatherOrHusbandName || '',
      languagesKnown: details.languages_known || details.languagesKnown || '',
      identificationMark: details.identification_mark || details.identificationMark || '',
      postHeld: details.post_held || details.postHeld || '',
      aadhaarNumber: details.aadhaar_number || details.aadhaarNumber || '',
      currentAddressLine1: details.current_address_line1 || details.currentAddressLine1 || '',
      currentAddressLine2: details.current_address_line2 || details.currentAddressLine2 || '',
      currentCityVillage: details.current_city_village || details.currentCityVillage || '',
      currentDistrict: details.current_district || details.currentDistrict || '',
      currentState: details.current_state || details.currentState || '',
      currentPinCode: details.current_pin_code || details.currentPinCode || '',
      permanentAddressLine1: details.permanent_address_line1 || details.permanentAddressLine1 || '',
      permanentAddressLine2: details.permanent_address_line2 || details.permanentAddressLine2 || '',
      permanentState: details.permanent_state || details.permanentState || '',
      permanentDistrict: details.permanent_district || details.permanentDistrict || '',
      permanentCityVillage: details.permanent_city_village || details.permanentCityVillage || '',
      permanentPinCode: details.permanent_pin_code || details.permanentPinCode || '',
      sameAsCurrent: Boolean(details.same_as_current ?? details.sameAsCurrent ?? false),
      witness1Name: details.witness1_name || details.witness1Name || '',
      witness1Phone: details.witness1_phone || details.witness1Phone || '',
      witness1Aadhaar: details.witness1_aadhaar || details.witness1Aadhaar || '',
      witness2Name: details.witness2_name || details.witness2Name || '',
      witness2Phone: details.witness2_phone || details.witness2Phone || '',
      witness2Aadhaar: details.witness2_aadhaar || details.witness2Aadhaar || '',
      vehicleMake: details.vehicle_make || details.vehicleMake || '',
      vehicleModel: details.vehicle_model || details.vehicleModel || '',
      vehicleRegistration: details.vehicle_registration || details.vehicleRegistration || '',
      vehicleRegistrationUrl: details.vehicle_registration_url || details.vehicleRegistrationUrl || '',
      promissoryNote: details.promissory_note_url || details.promissoryNote || '',
      aadhaarFront: details.aadhaar_front_url || details.aadhaarFront || '',
      aadhaarBack: details.aadhaar_back_url || details.aadhaarBack || '',
      officeState: details.office_state || details.officeState || '',
      officeDistrict: details.office_district || details.officeDistrict || '',
      officeCityVillage: details.office_city_village || details.officeCityVillage || '',
      range: details.range_field || details.range || '',
      rank: details.rank || '',
      branch: details.branch || '',
      history: details.history || [],
    };
  };

  const refreshUserDetails = async () => {
    try {
      const response = await api.getCurrentUser();
      if (response.user) {
        setCurrentUser(response.user);
        if (response.details) {
          setUserDetails(mapUserDetails(response.details));
        }
      }
    } catch (error) {
      console.error('Failed to refresh user details:', error);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const adminAccessMatch = window.location.hash.match(/(?:^#|&)admin-access=([^&]+)/);
      if (adminAccessMatch) {
        api.setToken(decodeURIComponent(adminAccessMatch[1]));
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
      const token = api.getToken();
      if (token) {
        try {
          const response = await api.getCurrentUser();
          if (response.user) {
            setCurrentUser(response.user);
            if (response.details) {
              setUserDetails(mapUserDetails(response.details));
            }
          } else {
            api.logout();
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          api.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const beginLogin = async (email: string, password: string): Promise<{ otpRequired: boolean; message?: string }> => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const response = await api.login(normalizedEmail, password);
      if (response.success && response.otpRequired) {
        return { otpRequired: true, message: response.message };
      }
      // Backward compatibility: if token is returned directly, treat as logged in
      if (response.success && response.user && response.token) {
        setCurrentUser(response.user);
        localStorage.setItem('userLoginTime', Date.now().toString());
        await refreshUserDetails();
        return { otpRequired: false };
      }
      return { otpRequired: false };
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const verifyLoginOtp = async (email: string, otp: string): Promise<boolean> => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const response = await api.verifyOtp(normalizedEmail, otp.trim(), 'login');
      if (response.success && response.user) {
        setCurrentUser(response.user);
        localStorage.setItem('userLoginTime', Date.now().toString());
        await refreshUserDetails();
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Verify OTP error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, fullName: string, phone?: string): Promise<boolean> => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const response = await api.register(normalizedEmail, password, fullName, phone);

      if (response.success) {
        if (response.user) {
          setCurrentUser({
            uid: response.user.uid,
            email: response.user.email,
            displayName: fullName,
            role: 'user'
          });
        }
        localStorage.setItem('userLoginTime', Date.now().toString());
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const googleLogin = async (credential: string): Promise<'ok' | 'failed'> => {
    try {
      const response = await api.googleLogin(credential);
      if (response.success && response.user && response.token) {
        setCurrentUser(response.user);
        localStorage.setItem('userLoginTime', Date.now().toString());
        await refreshUserDetails();
        return 'ok';
      }
      return 'failed';
    } catch (error: any) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      api.logout();
      setCurrentUser(null);
      setUserDetails(null);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userDetails,
    loading,
    beginLogin,
    verifyLoginOtp,
    googleLogin,
    register,
    logout,
    refreshUserDetails,
  };

  return (
    <CloudflareAuthContext.Provider value={value}>
      {!loading && children}
    </CloudflareAuthContext.Provider>
  );
};

export default CloudflareAuthContext;
